import { computed, readonly, ref } from 'vue'
import { userMessage } from '../../services/api'
import { addCapturedLeads, getCapturedLeads, type AddCapturedLeadResult, type CapturedLead } from '../../services/dialerApi'

export interface SelectableLead extends CapturedLead {
  selected: boolean
  inQueue: boolean
}

// The server's outcomes, sorted the one way every surface reads them.
export function tallyOutcomes(results: AddCapturedLeadResult[]): { added: number; already: number; skipped: number } {
  let added = 0
  let already = 0
  for (const r of results) {
    if (r.outcome === 'added' || r.outcome === 'created_and_added') added++
    else if (r.outcome === 'already_member') already++
  }
  return { added, already, skipped: results.length - added - already }
}

// The rep's recent LeadIQ captures as a pick list, for first-time onboarding and the
// queue's own pull. Everyone offered starts selected, so the selection is kept as the
// exclusions.
export function useCapturedLeads(opts: { inQueue?: (lead: CapturedLead) => boolean } = {}) {
  const leads = ref<CapturedLead[]>([])
  const unselected = ref<Set<string>>(new Set())
  const loading = ref(false)
  const loadError = ref<string | null>(null)
  const adding = ref(false)
  const addError = ref<string | null>(null)
  // A read that lands after reset(), or after a newer read, is dropped.
  let loadSeq = 0

  const selectable = computed<SelectableLead[]>(() =>
    leads.value.map((lead) => {
      const inQueue = opts.inQueue?.(lead) ?? false
      return { ...lead, inQueue, selected: !inQueue && !unselected.value.has(lead.lead_id) }
    }),
  )
  const addable = computed(() => selectable.value.filter((lead) => !lead.inQueue))
  const selectedCount = computed(() => selectable.value.filter((lead) => lead.selected).length)
  const allSelected = computed(() => addable.value.length > 0 && addable.value.every((lead) => lead.selected))
  const hasAddable = computed(() => addable.value.length > 0)

  async function load(): Promise<void> {
    const seq = ++loadSeq
    loading.value = true
    loadError.value = null
    unselected.value = new Set()
    leads.value = []
    try {
      const result = await getCapturedLeads()
      if (seq !== loadSeq) return
      leads.value = result
    } catch (e: any) {
      if (seq !== loadSeq) return
      console.warn('[useCapturedLeads] reading captured leads failed', e)
      loadError.value = userMessage(e, 'Something went wrong on our side. Try again.')
    } finally {
      if (seq === loadSeq) loading.value = false
    }
  }

  function toggleLead(leadId: string): void {
    if (selectable.value.find((lead) => lead.lead_id === leadId)?.inQueue) return
    const next = new Set(unselected.value)
    if (next.has(leadId)) next.delete(leadId)
    else next.add(leadId)
    unselected.value = next
  }

  function toggleAll(): void {
    unselected.value = allSelected.value ? new Set(addable.value.map((lead) => lead.lead_id)) : new Set()
  }

  // The server's outcomes once at least one person is in the queue; null when nothing was
  // sent, the request failed, or nobody could be queued (the last two said in addError).
  async function add(): Promise<AddCapturedLeadResult[] | null> {
    if (adding.value) return null
    const ids = selectable.value.filter((lead) => lead.selected).map((lead) => lead.lead_id)
    if (!ids.length) return null
    adding.value = true
    addError.value = null
    try {
      const results = await addCapturedLeads(ids)
      const { added, already } = tallyOutcomes(results)
      if (!added && !already) {
        addError.value = 'None of them could be added to your call list right now.'
        return null
      }
      return results
    } catch (e: any) {
      addError.value = userMessage(e, 'Couldn’t add them to your call list. Try again.')
      return null
    } finally {
      adding.value = false
    }
  }

  function reset(): void {
    loadSeq++
    loading.value = false
    leads.value = []
    unselected.value = new Set()
    loadError.value = null
    addError.value = null
  }

  return {
    leads: selectable,
    selectedCount,
    allSelected,
    hasAddable,
    loading: readonly(loading),
    loadError: readonly(loadError),
    adding: readonly(adding),
    addError: readonly(addError),
    load,
    toggleLead,
    toggleAll,
    add,
    reset,
  }
}
